import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Twitter,
  Instagram,
  Youtube,
  Mail,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import kinoraLogo from '@shared-assets/kinora_logo.svg';
import { cn } from '@/lib/utils';
import { guildAuthUrl } from '@/lib/guildUrl';

const SECTIONS = [
  {
    chapter: '01',
    title: 'Watch',
    links: [
      { name: 'Home', href: '/home', internal: true },
      { name: 'Browse catalogue', href: '/browse', internal: true },
      { name: 'Crowdfund', href: '/crowdfund', internal: true },
      { name: 'Plans & pricing', href: '/pricing', internal: true },
    ],
  },
  {
    chapter: '02',
    title: 'Creators',
    links: [
      {
        name: 'What is the Guild?',
        href: '/guild',
        internal: true,
      },
      {
        name: 'Join the Kinora Guild',
        href: guildAuthUrl(),
        internal: false,
      },
      {
        name: 'Kinora Studio (publish)',
        href: 'https://publish.slicetv.app',
        internal: false,
      },
    ],
  },
  {
    chapter: '03',
    title: 'Company',
    links: [
      { name: 'About Kinora', href: '/about', internal: true },
      { name: 'Contact', href: 'mailto:hello@kinora.tv', internal: false },
    ],
  },
  {
    chapter: '04',
    title: 'Legal',
    links: [
      { name: 'Terms of service', href: '/terms', internal: true },
      { name: 'Privacy policy', href: '/privacy', internal: true },
    ],
  },
];

const SOCIAL = [
  { icon: Twitter, href: '#', label: 'Twitter' },
  { icon: Instagram, href: '#', label: 'Instagram' },
  { icon: Youtube, href: '#', label: 'YouTube' },
];

const FooterLink = ({ link }) => {
  const className =
    'group inline-flex items-baseline gap-1.5 text-[0.95rem] text-muted-foreground hover:text-primary transition-colors';

  if (link.internal) {
    return (
      <Link to={link.href} className={className}>
        <span className="border-b border-transparent group-hover:border-primary/60 pb-px">
          {link.name}
        </span>
      </Link>
    );
  }

  const external =
    link.href.startsWith('http') || link.href.startsWith('mailto:');

  return (
    <a
      href={link.href}
      target={external && link.href.startsWith('http') ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={className}
    >
      <span className="border-b border-transparent group-hover:border-primary/60 pb-px">
        {link.name}
      </span>
      {link.href.startsWith('http') && (
        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </a>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitted(true);
    setEmail('');
    setTimeout(() => setSubmitted(false), 3500);
  };

  return (
    <footer className="relative bg-background border-t border-border/40 mt-20">
      <style>{`
        .foot-display { font-family: 'Fraunces', ui-serif, Georgia, serif; font-optical-sizing: auto; }
        .foot-mono { font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace; }
      `}</style>

      {/* ══ Masthead + newsletter ══ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-[480px] h-[480px] rounded-full bg-primary/[0.05] blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start">
          {/* Left — brand statement */}
          <div>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="foot-mono text-[10px] tracking-[0.22em] uppercase text-primary/80">
                Colophon
              </span>
              <div className="h-px flex-1 bg-border/50" />
            </div>
            <h2 className="foot-display font-medium text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] leading-[1.02] tracking-tight text-foreground max-w-lg">
              Independent cinema,
              <br />
              <span className="italic text-muted-foreground">directly funded.</span>
            </h2>
            <p className="mt-5 text-muted-foreground max-w-md leading-relaxed">
              Kinora is a streaming home for indie films, series, and documentaries — where every subscription sends slices to the creators you choose.
            </p>
          </div>

          {/* Right — newsletter card */}
          <aside className="w-full">
            <div className="border border-border/50 rounded-sm bg-card/30 backdrop-blur-sm p-6 md:p-8">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="foot-mono text-[10px] tracking-[0.22em] uppercase text-primary/80">
                  The newsletter
                </span>
                <div className="h-px flex-1 bg-border/50" />
              </div>
              <h3 className="foot-display text-xl md:text-2xl leading-tight text-foreground mb-2">
                New cuts, every other week.
              </h3>
              <p className="foot-display italic text-sm text-muted-foreground mb-5">
                What's arriving, what's worth watching, what's worth funding.
              </p>
              <form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-2"
              >
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 h-11 bg-background/60 border-border/60 rounded-full px-4 text-sm placeholder:italic placeholder:font-display placeholder:text-muted-foreground/60"
                />
                <Button
                  type="submit"
                  className="h-11 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                >
                  {submitted ? 'Subscribed' : 'Subscribe'}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </form>
              <p className="foot-mono text-[9px] tracking-[0.22em] uppercase text-muted-foreground/70 mt-4">
                No spam · unsubscribe anytime
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* ══ Link grid ══ */}
      <section className="border-t border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {SECTIONS.map((section) => (
              <div key={section.title}>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="foot-mono text-[10px] tracking-[0.22em] uppercase text-primary/80">
                    {section.chapter}
                  </span>
                  <h4 className="foot-display text-lg leading-none tracking-tight text-foreground">
                    {section.title}
                  </h4>
                </div>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ Bottom strip ══ */}
      <section className="border-t border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          {/* Left — mark + copyright */}
          <div className="flex items-center gap-4">
            <Link to="/home" className="flex items-center gap-2 group">
              <img
                src={kinoraLogo}
                alt="Kinora"
                className="h-6 w-auto select-none"
                draggable={false}
              />
              <span className="foot-display text-base text-foreground group-hover:text-primary transition-colors">
                Kinora
              </span>
            </Link>
            <span className="hidden sm:inline h-3 w-px bg-border/70" />
            <span className="foot-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground tabular-nums">
              © {currentYear} · All rights reserved
            </span>
          </div>

          {/* Right — social + contact */}
          <div className="flex items-center gap-2">
            {SOCIAL.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                className={cn(
                  'h-9 w-9 rounded-full border border-border/50 bg-card/30 inline-flex items-center justify-center',
                  'text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </a>
            ))}
            <span className="hidden sm:inline h-3 w-px bg-border/70 mx-2" />
            <a
              href="mailto:hello@kinora.tv"
              className="group inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border/50 bg-card/30 foot-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
            >
              <Mail className="h-3 w-3" />
              hello@kinora.tv
            </a>
          </div>
        </div>
      </section>
    </footer>
  );
};

export default Footer;
